import {
  Alert,
  AutocompleteSearchBox,
  Box,
  Grid2 as Grid,
  PlatformDialog,
  TextField,
  Typography,
} from "@platform/style";
import * as React from "react";
import { Dispatch, SetStateAction, useState } from "react";
import { fetchUsers } from "./apiClient";
import { fetchWithToken, unsetContext } from "@platform/core";
import { WORK_ITEMS_BFF_API_BASE } from "../../platform-workitems";
import { reportError } from "../../utils/errors";
import {
  ChatBubbleProps,
  ChatResponse,
  getErrorBubbles,
  ModalResponseHandler,
} from "@platform/chatwindow-utils";
import { timeStamp } from "@platform-static/string-utils";
import { ASSIGN_TO_WORKER_COMMAND_NAME } from "../../commands/assignWorkItem/assignWorkItemToWorkerCommand";
import { WorkItem } from "../../models/WorkItem";
import { AutocompleteInputChangeReason } from "@mui/material";
import { getIdentifier } from "../../utils/getIdentifier";

interface AssignWorkToWorkerDialogProps {
  open: boolean;
  onClose?: () => void;
  setSnackBarState?: Dispatch<SetStateAction<boolean>>;
  setSnackBarMessage?: Dispatch<SetStateAction<string>>;
  workItemId: string;
  handleUpdatesForWorkItem: Function;
  onError?: Dispatch<SetStateAction<string | null>>;
  refetch?: Function;
  setAssignWorkerOpen: Dispatch<SetStateAction<boolean>>;
  fromCommand?: boolean;
  modalResponseHandler?: ModalResponseHandler;
  workItem?: WorkItem;
  workItemAssignedTo?: boolean;
}

export const AssignWorkToWorkerDialog = (props: AssignWorkToWorkerDialogProps) => {
  const [offset, setOffset] = useState(0);
  const today = new Date().toLocaleDateString();
  const [loading, setLoading] = useState(false);
  const [oktaUsers, setOktaUsers] = useState<{ key: string; name: string; sentence: string }[]>([]);
  const [fieldName, setFieldName] = useState("AssignWorkItemToWorker");
  const [searchTerm, setSearchTerm] = useState("");
  const [comment, setComment] = React.useState<string | undefined>();
  const [selectedOption, setSelectedOption] = useState<any>(null);
  const [fetchUsersError, setFetchUsersError] = React.useState<string | null>(null);
  const [prevSelectedOption, setPrevSelectedOption] = React.useState<any>(null);
  const wasPreviouslyAssignedFromGrid = props.workItemAssignedTo;
  const wasPreviouslyAssignedFromCommand = !!props.workItem?.AssignedTo;

  React.useEffect(() => {
    let delayFunctionCall: string | number | NodeJS.Timeout | undefined;
    if (searchTerm?.length >= 2) {
      delayFunctionCall = setTimeout(() => {
        fetchUsers(searchTerm, setLoading, setOktaUsers, setFetchUsersError).then();
      }, 700);
    }
    return () => clearTimeout(delayFunctionCall);
  }, [searchTerm]);

  React.useEffect(() => {
    if (!props.open) {
      setSelectedOption(null);
      setPrevSelectedOption(null);
      setOktaUsers([]);
      setLoading(false);
      setSearchTerm("");
      setFetchUsersError(null);
    }
  }, [props.open]);

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setComment(value.trim() === "" ? undefined : value);
  };

  const assignFromWorkItemGrid = async () => {
    const requestBody: any = {
      AssignTo: `${selectedOption.key}`,
    };
    if (comment !== undefined) {
      requestBody.Comment = comment;
    }

    await fetchWithToken(`${WORK_ITEMS_BFF_API_BASE}/${props.workItemId}/assign`, {
      method: "POST",
      body: JSON.stringify(requestBody),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(JSON.stringify(response));
        }
        return response.json();
      })
      .then((result) => {
        if (props.refetch) {
          props.refetch();
        }
        if (props.setSnackBarMessage) {
          const message = wasPreviouslyAssignedFromGrid
            ? `Work Item has been reassigned to ${selectedOption.name}`
            : `Work Item has been assigned to ${selectedOption.name}`;
          props.setSnackBarMessage(message);
        }
        if (props.setSnackBarState) {
          props.setSnackBarState(true);
        }
        props.handleUpdatesForWorkItem(result, false);
        props.setAssignWorkerOpen(false);
      })
      .catch((error) => {
        if (props.onError) {
          props.onError(
            `Unable to assign work item #${getIdentifier(props.workItemId)} to ${
              selectedOption.name
            } . Please try again.`
          );
        }
        reportError(error.message);
      });
    props.setAssignWorkerOpen(false);
  };

  const assignFromCommand = async () => {
    if (props.workItemId) {
      const requestBody: any = {
        AssignTo: `${selectedOption.key}`,
      };
      if (comment !== undefined) {
        requestBody.Comment = comment;
      }

      const response: ChatResponse = await fetchWithToken(
        `${WORK_ITEMS_BFF_API_BASE}/${props.workItemId}/assign`,
        {
          method: "POST",
          body: JSON.stringify(requestBody),
        }
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(JSON.stringify(response));
          }
          unsetContext("workItem");
          let chatBubbles: ChatBubbleProps[] = [];
          const message = wasPreviouslyAssignedFromCommand
            ? `Work Item ${getIdentifier(props.workItemId)} has been reassigned to ${
                selectedOption.name
              }.`
            : `Work Item ${getIdentifier(props.workItemId)} has been assigned to ${
                selectedOption.name
              }.`;
          chatBubbles.push({
            content: message,
            variant: "success",
          });
          if (props.workItem) {
            props.handleUpdatesForWorkItem(props.workItem, false);
          }
          return {
            chatBlock: {
              chatBubbles: chatBubbles,
              timestamp: timeStamp(),
            },
          } as ChatResponse;
        })
        .catch((error) => {
          reportError(error);
          return {
            chatBlock: {
              chatBubbles: getErrorBubbles(ASSIGN_TO_WORKER_COMMAND_NAME),
              timestamp: timeStamp(),
            },
          } as ChatResponse;
        });
      props.setAssignWorkerOpen(false);
      if (props.modalResponseHandler) {
        props.modalResponseHandler(response);
      }
    }
  };

  const handleAssignToWorker = async () => {
    if (!props.fromCommand) {
      await assignFromWorkItemGrid();
    } else {
      await assignFromCommand();
    }
  };

  const closeFetchUsersErrorAlert = () => {
    setFetchUsersError(null);
  };

  return (
    <PlatformDialog
      open={props.open}
      onSubmit={handleAssignToWorker}
      onClose={props.onClose}
      submitLabel={"Assign"}
      isSubmitDisabled={!selectedOption}
      title={"Assign work item"}
      maxWidth={490}
    >
      <Grid>
        <Box sx={{ marginTop: "30px" }}>
          {fetchUsersError && (
            <Alert
              onClose={closeFetchUsersErrorAlert}
              severity="error"
              sx={{
                width: "calc(100% - 34px)",
                borderColor: "error.main",
                borderWidth: "1px",
                borderStyle: "solid",
                mb: 3,
              }}
            >
              {fetchUsersError}
            </Alert>
          )}
          <AutocompleteSearchBox
            aria-describedby="AssignWorkItemToWorker"
            id="AssignWork"
            name="AssignWorkToWorker"
            label="People Search"
            options={oktaUsers}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            setFieldName={setFieldName}
            setOffset={setOffset}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            loading={loading}
            onInputChange={(
              _event?: React.SyntheticEvent,
              _value?: string,
              reason?: AutocompleteInputChangeReason
            ) => {
              // this routine may not be effective when passing false to selectionConstrained prop of AutocompleteSearchBox
              // do not copy for other usages of this component where selectionConstrained is not set to true
              if (reason === "input") {
                if (selectedOption) {
                  setPrevSelectedOption(selectedOption);
                }
                setSelectedOption(null);
              } else if (reason === "blur") {
                if (_value === prevSelectedOption?.sentence) {
                  setSelectedOption(prevSelectedOption);
                }
              } else if (reason === "clear") {
                setSelectedOption(null);
                setPrevSelectedOption(null);
              }
            }}
            selectionConstrained={true}
            autoHighlight={true}
            autoSelect={true}
            autoComplete={true}
          />
          <div style={{ display: "flex", alignItems: "center", marginTop: "60px" }}>
            <Grid>
              <Box sx={{ marginBottom: "20px" }}>
                <Grid container justifyContent={"space-between"}>
                  <Box>
                    <Typography variant={"body2"} align={"left"} sx={{ marginBottom: "10px" }}>
                      Add Comment
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={"body2"} align={"right"} sx={{ marginBottom: "10px" }}>
                      {today}
                    </Typography>
                  </Box>
                </Grid>
                <TextField
                  id="comment-textfield-id"
                  multiline
                  rows={2}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                    },
                    formHelperText: {
                      sx: {
                        display: "flex",
                        justifyContent: "flex-end",
                        fontSize: "13px",
                      },
                    },
                  }}
                  sx={{
                    width: "440px",
                  }}
                  helperText={
                    comment ? `Character Count: ${500 - comment.length}` : `Character Count: 500`
                  }
                  onChange={handleCommentChange}
                  name="comment-textfield-name"
                />
              </Box>
            </Grid>
          </div>
        </Box>
      </Grid>
    </PlatformDialog>
  );
};
